import React from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import LifecycleBadge from './LifecycleBadge'
import InformationSystemPassport from './InformationSystemPassport'
import InformationSystemGrcSections from './InformationSystemGrcSections'
import InformationSystemGraphView from './InformationSystemGraphView'
import InformationSystemCompletenessChecklist from './InformationSystemCompletenessChecklist'
import OperationalTrendWidget from './OperationalTrendWidget'
import ReadinessBlockersPanel from './ReadinessBlockersPanel'
import InformationSystemCriticalDataSection from './InformationSystemCriticalDataSection'
import InformationSystemAssetsSection from './InformationSystemAssetsSection'
import styles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import type { InformationSystemDetail } from '../../services/informationSystemsApi'
import type { OperationalSummaryRow } from '../../services/informationSystemOperationalApi'
import type { BulkAssignAssetRow } from './BulkAssignAssetsModal'
import type { PassportSection } from './informationSystemPassportTypes'
import {
  type InformationSystemPanelContext,
  panelContextTitle,
  drillDownToContext,
} from './informationSystemPanelContext'
import {
  DrillDownContent,
  panelContextToDrillTarget,
  useDrillDownData,
} from './operationalDrillDownContent'
import type { DrillDownTarget } from './operationalDrillDownContent'
import {
  TopVulnerabilitiesList,
  TopFailedControlsList,
  TopOpenRisksList,
} from './systemPostureRowParts'
import InformationSystemPanelIntelligence from './InformationSystemPanelIntelligence'
import ContextNav from './ContextNav'
import InformationSystemExecutivePanel from '../enterprise-operational/InformationSystemExecutivePanel'
import type { InformationSystemExecutiveSummary } from '../../services/enterpriseOperationalUxApi'

interface Props {
  system: InformationSystemDetail
  summaryRow?: OperationalSummaryRow | null
  executiveSummary?: InformationSystemExecutiveSummary | null
  panelContext: InformationSystemPanelContext
  onContextChange: (ctx: InformationSystemPanelContext) => void
  inventoryAssets: BulkAssignAssetRow[]
  onClose: () => void
  onDataChanged: () => Promise<void>
  onEdit: () => void
  onDelete: () => void
  onArchive: () => void
  onLifecycleChange: (status: string) => Promise<void>
  onLinkAsset: (assetId: string) => Promise<void>
  onUnlinkAsset: (assetId: string) => Promise<void>
  onBulkLink: (assetIds: string[]) => Promise<{ linked?: Array<string | number>; skipped?: Array<string | number>; errors?: Array<{ assetId: string | number; message: string }> }>
  onBulkUnlink: (assetIds: string[]) => Promise<{ unlinked?: Array<string | number>; errors?: Array<{ assetId: string | number; message: string }> }>
  linking?: boolean
}

export default function InformationSystemContextPanel({
  system,
  summaryRow,
  executiveSummary,
  panelContext,
  onContextChange,
  inventoryAssets,
  onClose,
  onDataChanged,
  ...passportHandlers
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const drillTarget = panelContextToDrillTarget(panelContext)
  const { loading, error, data } = useDrillDownData(drillTarget, token, orgId, t)

  const openFullPassport = () => {
    onContextChange({ kind: 'fullPassport', systemId: system.id, section: 'overview' })
  }

  const handleDrillDown = (target: DrillDownTarget) => {
    if (!target) return
    onContextChange(
      drillDownToContext({
        kind: target.kind,
        systemId: target.systemId || system.id,
        id: target.kind === 'risk' ? target.sourceId : target.id,
        label: target.label,
        sourceType: target.kind === 'risk' ? target.sourceType : undefined,
        sourceId: target.kind === 'risk' ? target.sourceId : undefined,
      })
    )
  }

  const body = renderContextBody()

  function renderContextBody() {
    if (panelContext.kind === 'fullPassport') {
      const section: PassportSection = panelContext.section || 'overview'
      return (
        <InformationSystemPassport
          system={system}
          inventoryAssets={inventoryAssets}
          embedded
          initialSection={section}
          focusedSection={section}
          hideSectionNav
          {...passportHandlers}
        />
      )
    }

    if (panelContext.kind === 'overview') {
      return (
        <section className={styles.contextSectionBody}>
          <InformationSystemCompletenessChecklist systemId={system.id} />
          {system.description ? <p className={styles.contextDescription}>{system.description}</p> : null}
          <div className={styles.contextOverviewActions}>
            <button
              type="button"
              className={styles.contextLinkBtn}
              onClick={() => onContextChange({ kind: 'assets', systemId: system.id })}
            >
              {t('assets.panelContext.manageAssets', 'Manage linked assets')}
              {system.linkedAssetsCount != null ? ` (${system.linkedAssetsCount})` : ''}
            </button>
            <button type="button" className={styles.contextLinkBtn} onClick={openFullPassport}>
              {t('assets.postureRow.openPassport', 'Open full passport')}
            </button>
          </div>
        </section>
      )
    }

    if (panelContext.kind === 'assets') {
      return (
        <InformationSystemAssetsSection
          system={system}
          inventoryAssets={inventoryAssets}
          onLinkAsset={passportHandlers.onLinkAsset}
          onUnlinkAsset={passportHandlers.onUnlinkAsset}
          onBulkLink={passportHandlers.onBulkLink}
          onBulkUnlink={passportHandlers.onBulkUnlink}
          linking={passportHandlers.linking}
        />
      )
    }

    if (panelContext.kind === 'criticalData') {
      return <InformationSystemCriticalDataSection system={system} onChanged={onDataChanged} />
    }

    if (panelContext.kind === 'standards') {
      return <InformationSystemGrcSections systemId={system.id} visibleSection="compliance" />
    }

    if (panelContext.kind === 'controls') {
      return <InformationSystemGrcSections systemId={system.id} visibleSection="controls" />
    }

    if (panelContext.kind === 'evidence') {
      return <InformationSystemGrcSections systemId={system.id} visibleSection="evidence" />
    }

    if (panelContext.kind === 'remediation') {
      return <InformationSystemGrcSections systemId={system.id} visibleSection="remediation" />
    }

    if (panelContext.kind === 'readiness') {
      return (
        <div className={styles.contextSectionBody}>
          <InformationSystemCompletenessChecklist systemId={system.id} />
          <OperationalTrendWidget systemId={system.id} />
          <ReadinessBlockersPanel systemId={system.id} />
          <InformationSystemGrcSections systemId={system.id} visibleSection="readiness-summary" />
        </div>
      )
    }

    if (panelContext.kind === 'graph') {
      return <InformationSystemGraphView systemId={system.id} />
    }

    if (panelContext.kind === 'executive') {
      return (
        <InformationSystemExecutivePanel
          summary={executiveSummary ?? null}
          systemName={system.name}
        />
      )
    }

    if (panelContext.kind === 'vulnerability') {
      if (drillTarget) {
        return (
          <div className={styles.contextSectionBody}>
            {loading ? <p className={styles.loadingText}>{t('common.loading')}</p> : null}
            {error ? <p className={styles.contextError}>{error}</p> : null}
            {!loading && !error && data ? (
              <DrillDownContent
                kind="vulnerability"
                data={data}
                t={t}
                target={drillTarget}
                systemId={system.id}
                token={token}
                orgId={orgId}
                onOpenGovernance={() => window.open('/governance/risks', '_blank')}
              />
            ) : null}
          </div>
        )
      }
      return (
        <div className={styles.contextSectionBody}>
          <TopVulnerabilitiesList
            items={(summaryRow?.topVulnerabilities || []).slice(0, 10)}
            empty={t('assets.postureRow.noVulnerabilities', 'No critical vulnerabilities')}
            onDrillDown={handleDrillDown}
            systemId={system.id}
          />
        </div>
      )
    }

    if (panelContext.kind === 'control') {
      if (drillTarget) {
        return (
          <div className={styles.contextSectionBody}>
            {loading ? <p className={styles.loadingText}>{t('common.loading')}</p> : null}
            {error ? <p className={styles.contextError}>{error}</p> : null}
            {!loading && !error && data ? (
              <DrillDownContent
                kind="control"
                data={data}
                t={t}
                target={drillTarget}
                systemId={system.id}
                token={token}
                orgId={orgId}
              />
            ) : null}
          </div>
        )
      }
      return (
        <div className={styles.contextSectionBody}>
          <TopFailedControlsList
            items={(summaryRow?.topFailedControls || []).slice(0, 10)}
            empty={t('assets.postureRow.noFailedControls', 'No failed controls')}
            onDrillDown={handleDrillDown}
            systemId={system.id}
          />
        </div>
      )
    }

    if (panelContext.kind === 'risk') {
      if (drillTarget) {
        return (
          <div className={styles.contextSectionBody}>
            {loading ? <p className={styles.loadingText}>{t('common.loading')}</p> : null}
            {error ? <p className={styles.contextError}>{error}</p> : null}
            {!loading && !error && data ? (
              <DrillDownContent
                kind="risk"
                data={data}
                t={t}
                target={drillTarget}
                systemId={system.id}
                token={token}
                orgId={orgId}
                onOpenGovernance={() => window.open('/governance/risks', '_blank')}
              />
            ) : null}
          </div>
        )
      }
      return (
        <div className={styles.contextSectionBody}>
          <TopOpenRisksList
            items={(summaryRow?.topOpenRisks || []).slice(0, 10)}
            empty={t('assets.postureRow.noOpenRisks', 'No open risks')}
            onDrillDown={handleDrillDown}
            systemId={system.id}
          />
        </div>
      )
    }

    return null
  }

  return (
    <aside className={`${styles.detailsPanel} ${styles.rightPanel}`}>
      <header className={styles.detailsPanelHeader}>
        <span className={styles.detailsPanelHeaderMain}>
          <span className={styles.detailsPanelTitleRow}>
            <h3 className={styles.detailsPanelTitle} title={system.name}>
              {system.name}
            </h3>
            <LifecycleBadge status={system.lifecycleStatus} />
          </span>
          {system.code ? <p className={styles.detailsPanelCode}>{system.code}</p> : null}
        </span>
        <button type="button" className={styles.panelCloseBtn} onClick={onClose} aria-label={t('assets.postureRow.closeDetails', 'Close')}>
          ×
        </button>
      </header>

      {panelContext.kind !== 'fullPassport' ? (
        <InformationSystemPanelIntelligence
          system={system}
          summaryRow={summaryRow}
          panelContext={panelContext}
          onContextChange={onContextChange}
        />
      ) : null}

      {panelContext.kind !== 'fullPassport' ? (
        <ContextNav panelContext={panelContext} onContextChange={onContextChange} />
      ) : null}

      <div className={styles.detailsPanelBody}>{body}</div>

      {panelContext.kind !== 'fullPassport' ? (
        <div className={styles.contextPanelFooter}>
          <button type="button" className={styles.contextLinkBtn} onClick={openFullPassport}>
            {t('assets.postureRow.openPassport', 'Open full passport')}
          </button>
        </div>
      ) : null}
    </aside>
  )
}
